import { useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import Loader from '../components/Loader';
import { isEntraSigninAvailable } from '../auth/capabilityPolicy';
import './AuthStatusPage.scss';

// /signin: Microsoft sign-in with no Mashlom page in front of it. Microsoft's
// page handles sign-in, account creation, password reset and MFA; the user comes
// back through /auth/callback. Errors from Microsoft land there too, never here,
// so this page cannot bounce the user in a loop.
export default function Signin() {
  const { authMethod, authCapabilities, authError, loginWithEntra, logout } = useContext(UserContext);
  // Only a Microsoft session goes straight in; a legacy session still signs in
  // with Microsoft here (leaving drops the legacy token).
  const signedInWithMicrosoft = authMethod === 'entra';
  const [error, setError] = useState('');
  const started = useRef(false);
  const available = isEntraSigninAvailable(authCapabilities);

  const start = async () => {
    setError('');
    try {
      // Replaces this page in the history: Back from Microsoft leaves /signin.
      await loginWithEntra({ intent: 'login', replaceHistory: true });
    } catch (startError) {
      console.error('Opening Microsoft sign-in failed:', startError);
      setError('לא הצלחנו לפתוח את דף ההתחברות. נסו שוב.');
    }
  };

  useEffect(() => {
    // A sign-in that just failed is shown, not retried: Microsoft would sign the
    // same account straight back in and fail again, in a loop.
    if (signedInWithMicrosoft || !available || authError || started.current) return;
    started.current = true;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedInWithMicrosoft, available, authError]);

  if (signedInWithMicrosoft) return <Navigate replace to="/" />;
  if (!available) return <Navigate replace to="/login" />;
  if (authError) {
    return (
      <div className="auth-status-page" dir="rtl">
        <h1>ההתחברות לא הושלמה</h1>
        <p role="alert">{authError}</p>
        {/* Signs out of Microsoft, so the next sign-in can use another account. */}
        <button type="button" className="auth-status-primary" onClick={() => logout()}>
          התחברות עם חשבון אחר
        </button>
        <Link to="/login" className="auth-status-link">כניסה עם סיסמה</Link>
      </div>
    );
  }
  if (!error) return <Loader isLoading={true} />;

  return (
    <div className="auth-status-page" dir="rtl">
      <h1>כניסה למשלום</h1>
      <p role="alert">{error}</p>
      <button type="button" className="auth-status-primary" onClick={start}>נסו שוב</button>
      <Link to="/login" className="auth-status-link">כניסה עם סיסמה</Link>
    </div>
  );
}
